import { GlobalPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ComponentRef, Injectable, Type, ViewContainerRef } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Observable, of, Subject, take } from 'rxjs';
import { DrawerCloseHistoryBehavior, DrawerEvents, DrawerState, DrawerWidth, IsClosed } from '../definitions/drawer.definitions';
import { FormBase } from '../definitions/forms/form.definitions';
import { DrawerBaseViewComponent } from '../views/drawer-base-view.component';

export type DrawerData<T> = T extends DrawerBaseViewComponent<infer U> ? U : never;
export type DrawerVoidy<T> = T extends DrawerBaseViewComponent<infer U> ? [void] extends [U] ? T : [null] extends [U] ? T : [undefined] extends [U] ? T : never : never;

@Injectable({
    providedIn: 'root'
})
export class DrawerManager {

    private _drawers: Drawer[] = [];
    private _component?: ComponentPortal<any>;
    private _clickedOutside: boolean = false;
    private _clickedEsc: boolean = false;
    private _historyBehaviorForCurrentClose: DrawerCloseHistoryBehavior = DrawerCloseHistoryBehavior.RemoveDrawerHistoryEntry;
    private _checkingIfCanClose: boolean = false;
    private _attemptingToCloseSubject: Subject<void> = new Subject();
    private _primaryActionClicked: boolean = false;
    private _currentDrawerClosed: Subject<void> = new Subject();
    private _previousDrawerLayoutState: {Size: DrawerWidth; State: DrawerState} | null = null;
    private _allDrawersClosed: Subject<void> = new Subject();
    private _previousBodyPaddingRight: string = '';

    constructor(private _Overlay: Overlay) {
        window.addEventListener('popstate', (event: PopStateEvent) => {
            if (event.state?.drawerDepth === this.numberOfDrawers) {
                return;
            }
            if (this.numberOfDrawers && this.currentDrawer.State !== DrawerState.Closed) {
                this.CloseDrawer(true);
            }
        });
    }

    CreateDrawer<T extends DrawerBaseViewComponent<any>>(componentType: Type<DrawerVoidy<T>>, data: void, viewContainerRef?: ViewContainerRef | null): Observable<DrawerEvents>;
    CreateDrawer<T extends DrawerBaseViewComponent<any>>(componentType: Type<T>, data: DrawerData<T>, viewContainerRef?: ViewContainerRef | null): Observable<DrawerEvents>;
    CreateDrawer<T extends DrawerBaseViewComponent<any>>(componentType: Type<T>, data: DrawerData<T>, viewContainerRef?: ViewContainerRef | null): Observable<DrawerEvents> {
        const events = new Subject<DrawerEvents>();
        const previousForm = FormBase.currentForm;
        if (this.numberOfDrawers === 0) {
            this._previousBodyPaddingRight = document.body.style.paddingRight;
        }
        const overlayRef = this._Overlay.create({
            height: '100%',
            maxHeight: '100%',
            hasBackdrop: true,
            backdropClass: this._drawers.length > 0 ? 'scss-drawer-background-transparent' : 'cdk-overlay-dark-backdrop',
            positionStrategy: new GlobalPositionStrategy().end(),
            scrollStrategy: this._Overlay.scrollStrategies.block()
        });

        overlayRef.backdropClick().subscribe(() => {
            this.ClickedOutside();
        });

        overlayRef.keydownEvents().subscribe(e => {
            if (e.key === 'Escape') {
                this.ClickedEsc();
            }
        });

        // Smooth animations on drawer open (stop API calls from returning)
        DrawerBaseViewComponent.BeginTransitionAnimation();
        setTimeout(() => {
            if (FormBase.currentForm === previousForm) {
                FormBase.currentForm = null;
            }
        }, 350);

        this._primaryActionClicked = false;
        this._component = new ComponentPortal<DrawerBaseViewComponent<T>>(componentType);
        if (viewContainerRef) {
            this._component.viewContainerRef = viewContainerRef;
        } else if (viewContainerRef === undefined) {
            console.warn('Calling CreateDrawer without a ViewContainer does not properly propagate Scope. Please call this.CreateDrawer if ' +
                         'in a BaseViewComponent, pass in a ViewContainer manually, or pass in null to stop showing this warning.');
        }

        this._drawers.push({
            State: DrawerState.Default,
            PreviousForm: previousForm,
            Events: events,
            Overlay: overlayRef,
            Size: DrawerWidth.Medium // Default size, can be changed by the drawer component after creation
        });

        const component = (overlayRef.attach(this._component) as ComponentRef<DrawerBaseViewComponent<T>>).instance;

        const currentDrawer = this.currentDrawer.Overlay.hostElement;
        currentDrawer.style.willChange = 'transform';
        setTimeout(() => {
            if (this.numberOfDrawers === 1) {
                const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
                const bodyPaddingRight = parseFloat(getComputedStyle(document.body).paddingRight) || 0;
                document.body.style.paddingRight = `${bodyPaddingRight + scrollbarWidth}px`;
                document.documentElement.classList.add('sb-drawer-open');
            }
        }, 175);
        requestAnimationFrame(() => {
            currentDrawer.classList.add('drawer-slide-in');
            const onDrawerAnimationEnd = (event: AnimationEvent) => {
                if (event.target !== currentDrawer || event.animationName !== 'drawerSlideIn') {
                    return;
                }
                currentDrawer.style.willChange = '';
                currentDrawer.classList.add('drawer-slide-in-done');
                currentDrawer.removeEventListener('animationend', onDrawerAnimationEnd);
            };
            currentDrawer.addEventListener('animationend', onDrawerAnimationEnd);
        });
        if (this.numberOfDrawers > 1) {
            const previousDrawer = this._drawers[this._drawers.length - 2];
            //For previous drawer - we just move the content and current drawer we slide everything(this is what UX requested)
            previousDrawer.Overlay.hostElement.classList.remove('drawer-slide-in');
            const previousDrawerLayout = this.GetDrawerLayout(previousDrawer) as HTMLElement;
            previousDrawerLayout.style.willChange = 'transform';
            requestAnimationFrame(() => {
                previousDrawerLayout.classList.add('drawer-slide-out');
                const onPreviousDrawerAnimationEnd = (event: AnimationEvent) => {
                    if (event.target !== previousDrawerLayout || event.animationName !== 'drawerSlideOut') {
                        return;
                    }
                    previousDrawerLayout.style.willChange = '';
                    previousDrawerLayout.removeEventListener('animationend', onPreviousDrawerAnimationEnd);
                };
                previousDrawerLayout.addEventListener('animationend', onPreviousDrawerAnimationEnd);
            });
            this._previousDrawerLayoutState = { Size: previousDrawer.Size, State: previousDrawer.State };
        }

        component.data = data;
        component.events = events;
        component.CloseDrawer = this.CloseDrawer.bind(this);

        component.PrimaryActionClicked = this.PrimaryActionClicked.bind(this);

        // Push onto history stack so that back button can be intercepted to close the drawer
        history.pushState({ drawerDepth: this._drawers.length }, document.title, window.location.href);

        return events;
    }

    CloseAllDrawers(skipHistoryBack: boolean = false): void {
        const historyBehavior = skipHistoryBack ? DrawerCloseHistoryBehavior.LeaveHistoryUnchanged : DrawerCloseHistoryBehavior.RemoveDrawerHistoryEntry;
        this._drawers.forEach(drawer => {
            const subscription = drawer.Events.subscribe(drawerEvents => {
                if (IsClosed(drawerEvents)) {
                    this._currentDrawerClosed.pipe(take(1)).subscribe(() => { 
                        this.CloseDrawer(false, historyBehavior);
                    });
                    subscription.unsubscribe();
                }
            })
        });
        this.CloseDrawer(false, historyBehavior);
    }

    CloseDrawer(closingFromBackButton: boolean = false, historyBehavior: DrawerCloseHistoryBehavior = DrawerCloseHistoryBehavior.RemoveDrawerHistoryEntry): Observable<void> {
        if (this._drawers.length) {
            if (!this._checkingIfCanClose) {
                this._previousDrawerLayoutState = { Size: this.currentDrawer.Size, State: this.currentDrawer.State };
                this._historyBehaviorForCurrentClose = closingFromBackButton ? DrawerCloseHistoryBehavior.BrowserBackAlreadyRemovedEntry : historyBehavior;
                this._checkingIfCanClose = true;
                this._attemptingToCloseSubject.next();
            }
            return this._currentDrawerClosed.pipe(take(1));
        } else {
            return of(undefined);
        }
    }

    FinishDrawerCloseAttempt(canClose: boolean) {
        if (canClose) {
            const currentDrawer = this.currentDrawer.Overlay.hostElement;
            if (this.numberOfDrawers === 1) {
                document.body.style.paddingRight = this._previousBodyPaddingRight;
                document.documentElement.classList.remove('sb-drawer-open');
                currentDrawer.classList.remove('drawer-slide-in');
                currentDrawer.classList.remove('drawer-slide-in-done');
                currentDrawer.classList.add('drawer-slide-close');
                const onDrawerAnimationEnd = (event: AnimationEvent) => {
                    if (event.target !== currentDrawer || event.animationName !== 'drawerClose') {
                        return;
                    }
                    this.CloseDrawerAfterAnimation();
                    currentDrawer.removeEventListener('animationend', onDrawerAnimationEnd);
                };
                currentDrawer.addEventListener('animationend', onDrawerAnimationEnd);
                this._previousDrawerLayoutState = null;
            } else if (this.numberOfDrawers > 1) {
                const previousDrawerLayout = this.GetDrawerLayout(this._drawers[this._drawers.length - 2]);
                // Previous drawer only slide content and current drawer slide drawer
                previousDrawerLayout.classList.remove('drawer-slide-in');
                currentDrawer.classList.remove('drawer-slide-in-done');
                previousDrawerLayout.classList.remove('drawer-slide-out');
                currentDrawer.classList.add('drawer-slide-close');
                previousDrawerLayout.classList.add('previous-drawer-slide-back');
                const onDrawerAnimationEnd = (event: AnimationEvent) => {
                    if (event.target !== currentDrawer || event.animationName !== 'drawerClose') {
                        return;
                    }
                    previousDrawerLayout.classList.remove('previous-drawer-slide-back');
                    this.CloseDrawerAfterAnimation();
                    currentDrawer.removeEventListener('animationend', onDrawerAnimationEnd);
                };
                currentDrawer.addEventListener('animationend', onDrawerAnimationEnd);
            }
        } else {
            this._checkingIfCanClose = false;

            if (this._historyBehaviorForCurrentClose === DrawerCloseHistoryBehavior.BrowserBackAlreadyRemovedEntry) {
                // Need to push onto the history stack otherwise the next press of the back button could trigger canDeactivate from the route guard or tab header
                history.pushState({ drawerDepth: this._drawers.length }, document.title, window.location.href);
            }
            this._historyBehaviorForCurrentClose = DrawerCloseHistoryBehavior.RemoveDrawerHistoryEntry;
        }
        this._clickedOutside = false;
        this._clickedEsc = false;
    }

    private GetDrawerLayout(drawer: Drawer): Element {
        return drawer.Overlay.hostElement.firstElementChild?.firstElementChild?.firstElementChild?.firstElementChild?.firstElementChild?.firstElementChild!;
    }

    private PrimaryActionClicked(clicked: boolean): void {
        this._primaryActionClicked = clicked;
    }

    private ClickedOutside(): void {
        this._clickedOutside = true;
        this.currentDrawer.Events.next(DrawerEvents.ClickedOutside);
        this.CloseAllDrawers();
    }

    private ClickedEsc(): void {
        this._clickedEsc = true;
        this.currentDrawer.Events.next(DrawerEvents.ClickedEsc);
        this.CloseAllDrawers();
    }

    private CloseDrawerAfterAnimation() {
        // do not attempt to close the drawer once it no longer exists
        if (!this.currentDrawer) {
            return;
        }
        
        this.currentDrawer.Overlay.dispose();
        FormBase.currentForm = this.currentDrawer.PreviousForm;
        this._checkingIfCanClose = false;
        this._component = undefined; // Destroy drawer component

        const currentDrawer = this._drawers.pop()!;
        const historyBehavior = this._historyBehaviorForCurrentClose;
        this._historyBehaviorForCurrentClose = DrawerCloseHistoryBehavior.RemoveDrawerHistoryEntry;

        // Only consume a browser history entry here, once the drawer has actually finished closing, so the history stack and the drawer stack never fall out of sync
        if (historyBehavior === DrawerCloseHistoryBehavior.RemoveDrawerHistoryEntry) {
            history.back();
        }

        if (this._primaryActionClicked) {
            currentDrawer.Events.next(DrawerEvents.ClosedFromPrimaryAction);
        } else if (this._clickedOutside) {
            currentDrawer.Events.next(DrawerEvents.ClosedFromClickedOutside);
        } else if (this._clickedEsc) {
            currentDrawer.Events.next(DrawerEvents.ClosedFromClickedEsc);
        } else {
            currentDrawer.Events.next(DrawerEvents.Closed);
        }
        currentDrawer.Events.complete();

        if (this.numberOfDrawers === 0) {
            this._allDrawersClosed.next();
        }

        this._currentDrawerClosed.next();      
    }

    get currentDrawer(): Drawer {
        return this._drawers[this._drawers.length - 1];
    }

    get drawers(): Drawer[] {
        return this._drawers;   
    }

    get attemptingToCloseSubject(): Subject<void> {
        return this._attemptingToCloseSubject;
    }

    get numberOfDrawers(): number {
        return this._drawers.length;
    }

    get previousDrawerLayoutState(): {Size: DrawerWidth; State: DrawerState} | null {
        return this._previousDrawerLayoutState;
    }

    get anyDrawerFormDirty(): boolean {
        return this._drawers.some(drawer => drawer.PreviousForm?.isDirty);
    }

    get allDrawersClosed(): Observable<void> {
        return this.numberOfDrawers ? this._allDrawersClosed.pipe(take(1)) : of(undefined);
    }

    get allDrawersClosedAfterOpening(): Observable<void> {
        return this._allDrawersClosed.pipe(take(1));
    }
}

type Drawer = {
    State: DrawerState,
    PreviousForm: FormGroup<any> | null,
    Events: Subject<DrawerEvents>,
    Overlay: OverlayRef,
    Size: DrawerWidth,
    LayoutElement?: HTMLElement
};
